import type * as SchemaTypes from '../types';

import type { ApplicationMarketTypeFragment_DefaultApplicationMarket_Fragment, ApplicationMarketTypeFragment_NewZealandApplicationMarket_Fragment, ApplicationMarketTypeFragment_SingaporeApplicationMarket_Fragment } from './ApplicationMarketTypeFragment';
import type { DealerMarketDataFragment } from './DealerMarketData';
import type { BankDealerMarketDataFragment } from './BankDealerMarketData';
import type { NzFeesDealerMarketDataFragment } from './NzFeesDealerMarketData';
import type { TranslatedStringDataFragment } from './TranslatedStringData';
import type { InsurerEntrypointContextDataFragment } from './InsurerEntrypointContextData';
import type { DealerDisclaimersConfiguratorDataFragment } from './DealerPriceDisclaimerConfiguratorData';
import type { FlexibleDiscountDataFragment } from './FlexibleDiscountData';
import type { DealerApplicationFragmentFragment } from './DealerApplicationFragment';
import type { DealerContactFragmentFragment } from './DealerContactFragment';
import type { DealerSocialMediaFragmentFragment } from './DealerSocialMediaFragment';
import type { UploadFileWithPreviewFormDataFragment } from './UploadFileWithPreviewFormData';
import type { DealerDisclaimersFragmentFragment } from './DealerDisclaimersFragment';
import type { DealerIntegrationDetailsFragmentFragment } from './DealerIntegrationDetailsFragment';
import { gql } from '@apollo/client';
import { ApplicationMarketTypeFragmentFragmentDoc } from './ApplicationMarketTypeFragment';
import { DealerMarketDataFragmentDoc } from './DealerMarketData';
import { BankDealerMarketDataFragmentDoc } from './BankDealerMarketData';
import { NzFeesDealerMarketDataFragmentDoc } from './NzFeesDealerMarketData';
import { TranslatedStringDataFragmentDoc } from './TranslatedStringData';
import { InsurerEntrypointContextDataFragmentDoc } from './InsurerEntrypointContextData';
import { DealerDisclaimersConfiguratorDataFragmentDoc } from './DealerPriceDisclaimerConfiguratorData';
import { FlexibleDiscountDataFragmentDoc } from './FlexibleDiscountData';
import { DealerApplicationFragmentFragmentDoc } from './DealerApplicationFragment';
import { DealerContactFragmentFragmentDoc } from './DealerContactFragment';
import { DealerSocialMediaFragmentFragmentDoc } from './DealerSocialMediaFragment';
import { UploadFileWithPreviewFormDataFragmentDoc } from './UploadFileWithPreviewFormData';
import { DealerDisclaimersFragmentFragmentDoc } from './DealerDisclaimersFragment';
import { DealerIntegrationDetailsFragmentFragmentDoc } from './DealerIntegrationDetailsFragment';
export type StandardApplicationPublicAccessEntrypointContextDataFragment = (
  { __typename: 'StandardApplicationPublicAccessEntrypoint' }
  & Pick<SchemaTypes.StandardApplicationPublicAccessEntrypoint, 'id' | 'pathname' | 'displayName' | 'dealerIds'>
  & { applicationModule: (
    { __typename: 'StandardApplicationModule' }
    & Pick<SchemaTypes.StandardApplicationModule, 'id' | 'market' | 'vehicleModuleId' | 'bankModuleId' | 'insuranceModuleId' | 'scenarios' | 'myInfoSettingId' | 'isOcrEnabled' | 'hasDealerOptions' | 'includeDealerOptionsForFinancing' | 'showFromValueOnVehicleDetails' | 'bankDisplayPreference' | 'paymentSettingsId' | 'tradeIn' | 'isTradeInAmountVisible' | 'testDrive' | 'isFinancingOptional' | 'isInsuranceOptional' | 'showFinanceCalculator' | 'showInsuranceCalculator' | 'insurerDisplayPreference' | 'insurerIds'>
    & { marketType: (
      { __typename: 'DefaultApplicationMarket' }
      & ApplicationMarketTypeFragment_DefaultApplicationMarket_Fragment
    ) | (
      { __typename: 'NewZealandApplicationMarket' }
      & ApplicationMarketTypeFragment_NewZealandApplicationMarket_Fragment
    ) | (
      { __typename: 'SingaporeApplicationMarket' }
      & ApplicationMarketTypeFragment_SingaporeApplicationMarket_Fragment
    ), dealerPriceDisclaimer?: SchemaTypes.Maybe<(
      { __typename: 'DealerPriceDisclaimer' }
      & { defaultValue: (
        { __typename: 'TranslatedString' }
        & TranslatedStringDataFragment
      ), overrides: Array<(
        { __typename: 'DealerPriceDisclaimerOverride' }
        & Pick<SchemaTypes.DealerPriceDisclaimerOverride, 'dealerId'>
        & { value: (
          { __typename: 'TranslatedString' }
          & TranslatedStringDataFragment
        ) }
      )> }
    )>, company: (
      { __typename: 'Company' }
      & Pick<SchemaTypes.Company, 'id' | 'countryCode'>
    ), promoCodeModule?: SchemaTypes.Maybe<(
      { __typename: 'AdyenPaymentModule' }
      & Pick<SchemaTypes.AdyenPaymentModule, 'id' | 'displayName'>
    ) | (
      { __typename: 'AppointmentModule' }
      & Pick<SchemaTypes.AppointmentModule, 'id' | 'displayName'>
    ) | (
      { __typename: 'AudiCalculationModule' }
      & Pick<SchemaTypes.AudiCalculationModule, 'id' | 'displayName'>
    ) | (
      { __typename: 'AudiFinancingModule' }
      & Pick<SchemaTypes.AudiFinancingModule, 'id' | 'displayName'>
    ) | (
      { __typename: 'AudiSalesForceModule' }
      & Pick<SchemaTypes.AudiSalesForceModule, 'id' | 'displayName'>
    ) | (
      { __typename: 'AutoplayModule' }
      & Pick<SchemaTypes.AutoplayModule, 'id' | 'displayName'>
    ) | (
      { __typename: 'BankModule' }
      & Pick<SchemaTypes.BankModule, 'id' | 'displayName'>
    ) | (
      { __typename: 'BasicSigningModule' }
      & Pick<SchemaTypes.BasicSigningModule, 'id' | 'displayName'>
    ) | (
      { __typename: 'BmwModule' }
      & Pick<SchemaTypes.BmwModule, 'id' | 'displayName'>
    ) | (
      { __typename: 'CapModule' }
      & Pick<SchemaTypes.CapModule, 'id' | 'displayName'>
    ) | (
      { __typename: 'ConfiguratorModule' }
      & Pick<SchemaTypes.ConfiguratorModule, 'id' | 'displayName'>
    ) | (
      { __typename: 'ConsentsAndDeclarationsModule' }
      & Pick<SchemaTypes.ConsentsAndDeclarationsModule, 'id' | 'displayName'>
    ) | (
      { __typename: 'CtsModule' }
      & Pick<SchemaTypes.CtsModule, 'id' | 'displayName'>
    ) | (
      { __typename: 'EventApplicationModule' }
      & Pick<SchemaTypes.EventApplicationModule, 'id' | 'displayName'>
    ) | (
      { __typename: 'FinderApplicationPrivateModule' }
      & Pick<SchemaTypes.FinderApplicationPrivateModule, 'id' | 'displayName'>
    ) | (
      { __typename: 'FinderApplicationPublicModule' }
      & Pick<SchemaTypes.FinderApplicationPublicModule, 'id' | 'displayName'>
    ) | (
      { __typename: 'FinderVehicleManagementModule' }
      & Pick<SchemaTypes.FinderVehicleManagementModule, 'id' | 'displayName'>
    ) | (
      { __typename: 'FiservPaymentModule' }
      & Pick<SchemaTypes.FiservPaymentModule, 'id' | 'displayName'>
    ) | (
      { __typename: 'GiftVoucherModule' }
      & Pick<SchemaTypes.GiftVoucherModule, 'id' | 'displayName'>
    ) | (
      { __typename: 'InsuranceModule' }
      & Pick<SchemaTypes.InsuranceModule, 'id' | 'displayName'>
    ) | (
      { __typename: 'LabelsModule' }
      & Pick<SchemaTypes.LabelsModule, 'id' | 'displayName'>
    ) | (
      { __typename: 'LocalCustomerManagementModule' }
      & Pick<SchemaTypes.LocalCustomerManagementModule, 'id' | 'displayName'>
    ) | (
      { __typename: 'MaintenanceModule' }
      & Pick<SchemaTypes.MaintenanceModule, 'id' | 'displayName'>
    ) | (
      { __typename: 'MobilityModule' }
      & Pick<SchemaTypes.MobilityModule, 'id' | 'displayName'>
    ) | (
      { __typename: 'MyInfoModule' }
      & Pick<SchemaTypes.MyInfoModule, 'id' | 'displayName'>
    ) | (
      { __typename: 'NamirialSigningModule' }
      & Pick<SchemaTypes.NamirialSigningModule, 'id' | 'displayName'>
    ) | (
      { __typename: 'PayGatePaymentModule' }
      & Pick<SchemaTypes.PayGatePaymentModule, 'id' | 'displayName'>
    ) | (
      { __typename: 'PorscheMasterDataModule' }
      & Pick<SchemaTypes.PorscheMasterDataModule, 'id' | 'displayName'>
    ) | (
      { __typename: 'PorschePaymentModule' }
      & Pick<SchemaTypes.PorschePaymentModule, 'id' | 'displayName'>
    ) | (
      { __typename: 'PromoCodeModule' }
      & Pick<SchemaTypes.PromoCodeModule, 'id' | 'displayName'>
    ) | (
      { __typename: 'SdmModule' }
      & Pick<SchemaTypes.SdmModule, 'id' | 'displayName'>
    ) | (
      { __typename: 'SimpleVehicleManagementModule' }
      & Pick<SchemaTypes.SimpleVehicleManagementModule, 'id' | 'displayName'>
    ) | (
      { __typename: 'StandardApplicationModule' }
      & Pick<SchemaTypes.StandardApplicationModule, 'id' | 'displayName'>
    ) | (
      { __typename: 'TradeInModule' }
      & Pick<SchemaTypes.TradeInModule, 'id' | 'displayName'>
    ) | (
      { __typename: 'TtbPaymentModule' }
      & Pick<SchemaTypes.TtbPaymentModule, 'id' | 'displayName'>
    ) | (
      { __typename: 'UserlikeChatbotModule' }
      & Pick<SchemaTypes.UserlikeChatbotModule, 'id' | 'displayName'>
    ) | (
      { __typename: 'WebsiteModule' }
      & Pick<SchemaTypes.WebsiteModule, 'id' | 'displayName'>
    ) | (
      { __typename: 'WhatsappLiveChatModule' }
      & Pick<SchemaTypes.WhatsappLiveChatModule, 'id' | 'displayName'>
    )>, paymentSettings?: SchemaTypes.Maybe<(
      { __typename: 'AdyenPaymentSetting' }
      & Pick<SchemaTypes.AdyenPaymentSetting, 'id' | 'displayName' | 'paymentModuleId'>
    ) | (
      { __typename: 'FiservPaymentSetting' }
      & Pick<SchemaTypes.FiservPaymentSetting, 'id' | 'displayName' | 'paymentModuleId'>
    ) | (
      { __typename: 'PayGatePaymentSetting' }
      & Pick<SchemaTypes.PayGatePaymentSetting, 'id' | 'displayName' | 'paymentModuleId'>
    ) | (
      { __typename: 'PorschePaymentSetting' }
      & Pick<SchemaTypes.PorschePaymentSetting, 'id' | 'displayName' | 'paymentModuleId'>
    ) | (
      { __typename: 'TtbPaymentSetting' }
      & Pick<SchemaTypes.TtbPaymentSetting, 'id' | 'displayName' | 'paymentModuleId'>
    )>, insurers: Array<(
      { __typename: 'Insurer' }
      & InsurerEntrypointContextDataFragment
    )>, dealerVehicles: Array<(
      { __typename: 'DealerVehicles' }
      & Pick<SchemaTypes.DealerVehicles, 'dealerId' | 'vehicleSuiteIds'>
      & { vehicles: Array<(
        { __typename: 'FinderVehicle' }
        & Pick<SchemaTypes.FinderVehicle, 'id'>
        & { name: (
          { __typename: 'TranslatedString' }
          & TranslatedStringDataFragment
        ) }
      ) | (
        { __typename: 'LocalMake' }
        & Pick<SchemaTypes.LocalMake, 'id'>
        & { name: (
          { __typename: 'TranslatedString' }
          & TranslatedStringDataFragment
        ) }
      ) | (
        { __typename: 'LocalModel' }
        & Pick<SchemaTypes.LocalModel, 'id'>
        & { name: (
          { __typename: 'TranslatedString' }
          & TranslatedStringDataFragment
        ) }
      ) | (
        { __typename: 'LocalVariant' }
        & Pick<SchemaTypes.LocalVariant, 'id'>
        & { name: (
          { __typename: 'TranslatedString' }
          & TranslatedStringDataFragment
        ) }
      )>, dealer: (
        { __typename: 'Dealer' }
        & Pick<SchemaTypes.Dealer, 'id'>
        & { legalName: (
          { __typename: 'TranslatedString' }
          & TranslatedStringDataFragment
        ) }
      ) }
    )>, flexibleDiscount: (
      { __typename: 'FlexibleDiscount' }
      & FlexibleDiscountDataFragment
    ) }
  ), dealers: Array<(
    { __typename: 'Dealer' }
    & DealerApplicationFragmentFragment
  )> }
);

export const StandardApplicationPublicAccessEntrypointContextDataFragmentDoc = /*#__PURE__*/ gql`
    fragment StandardApplicationPublicAccessEntrypointContextData on StandardApplicationPublicAccessEntrypoint {
  id
  pathname
  displayName
  applicationModule {
    id
    market
    marketType {
      ...ApplicationMarketTypeFragment
    }
    dealerPriceDisclaimer {
      defaultValue {
        ...TranslatedStringData
      }
      overrides {
        dealerId
        value {
          ...TranslatedStringData
        }
      }
    }
    company {
      id
      countryCode
    }
    vehicleModuleId
    bankModuleId
    insuranceModuleId
    scenarios
    myInfoSettingId
    isOcrEnabled
    hasDealerOptions
    includeDealerOptionsForFinancing
    promoCodeModule {
      id
      displayName
    }
    showFromValueOnVehicleDetails
    bankDisplayPreference
    paymentSettingsId
    paymentSettings {
      id
      displayName
      paymentModuleId
    }
    tradeIn
    isTradeInAmountVisible
    testDrive
    isFinancingOptional
    isInsuranceOptional
    showFinanceCalculator
    showInsuranceCalculator
    insurerDisplayPreference
    insurerIds
    insurers {
      ...InsurerEntrypointContextData
    }
    dealerVehicles {
      dealerId
      vehicleSuiteIds
      vehicles {
        id
        name {
          ...TranslatedStringData
        }
      }
      dealer {
        id
        legalName {
          ...TranslatedStringData
        }
      }
    }
    flexibleDiscount {
      ...FlexibleDiscountData
    }
  }
  dealerIds
  dealers {
    ...DealerApplicationFragment
  }
}
    `;